<script lang="ts">
  import { numberWithCommas } from "$lib/utils"
  import { statsStore } from "$lib/stores/statsStore"
  import { t, getTextDirection } from "$lib/utils/i18n"
  import { Trophy } from "lucide-svelte"
  import WidgetLeaderboard from "./WidgetLeaderboard.svelte"
  import { defaultColorSettings } from "$lib/utils/colors"

  interface UserRankData {
    user_rank: number
    total_participants: number
    entries_ahead_of_next: number
    user_total_entries: number
  }

  let { competition, user, isLoading, showReferrals = false } = $props()
  let showLeaderboard = $state(false)
  let userRankData = $state<UserRankData | null>(null)
  let isLoadingRank = $state(false)

  // Get text direction for RTL support
  const textDirection = $derived(getTextDirection(competition?.language))
  const isRTL = $derived(textDirection === "rtl")

  // Get color settings with defaults
  const colorSettings = $derived(
    competition?.widgetCustomizations?.colorSettings || defaultColorSettings,
  )

  // Computed styles for dynamic CSS
  const leaderboardButtonStyle = $derived(`
    background: linear-gradient(to ${isRTL ? "left" : "right"}, ${colorSettings.gradientStart}, ${colorSettings.gradientEnd});
    border-color: ${colorSettings.borderColor};
    color: ${colorSettings.primaryTextColor};
    border-radius: ${colorSettings.statsSectionBorderRadius || "8px"};
  `)

  const leaderboardButtonHoverStyle = $derived(`
    background: linear-gradient(to ${isRTL ? "left" : "right"}, ${colorSettings.hoverGradientStart}, ${colorSettings.hoverGradientEnd});
    border-color: ${colorSettings.borderColor};
    color: ${colorSettings.primaryTextColor};
    border-radius: ${colorSettings.statsSectionBorderRadius || "8px"};
  `)

  const trophyIconStyle = $derived(`
    background-color: ${colorSettings.iconBgColor};
    color: ${colorSettings.iconColor};
  `)

  // Determine which stats to show based on settings
  const showYourEntries = $derived(
    competition?.widgetSettings?.statsSettings?.showYourEntries ?? true,
  )
  const showTotalEntries = $derived(
    competition?.widgetSettings?.statsSettings?.showTotalEntriesInStats ?? true,
  )
  const showTimeLeft = $derived(
    competition?.widgetSettings?.statsSettings?.showTimeLeft ?? true,
  )

  // Count visible stats for layout purposes
  const visibleStats = $derived(
    [showYourEntries, showTotalEntries, showTimeLeft].filter(Boolean),
  )
  const visibleStatsCount = $derived(visibleStats.length)

  async function fetchUserRank() {
    try {
      isLoadingRank = true
      const response = await fetch(`/api/competitions/user-rank`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          slug: competition.slug,
          userEmail: user.email,
        }),
      })

      if (response.ok) {
        const { userRank } = await response.json()
        userRankData = userRank
      }
    } catch (error) {
      console.error("Failed to fetch user rank:", error)
    } finally {
      isLoadingRank = false
    }
  }

  // Fetch user rank when component mounts
  $effect(() => {
    if (!isLoading && user) {
      fetchUserRank()
    }
  })

  // Clear user rank data when user logs out
  $effect(() => {
    if (!user) {
      userRankData = null
      showLeaderboard = false
    }
  })
</script>

{#if competition.widgetSettings?.showStats ?? true}
  {#if competition.widgetSettings?.hideStatsUntilLogin && !user}
    <!-- Show no stats if not logged in -->
  {:else if visibleStatsCount > 0}
    <div
      class="flex justify-between items-center gap-2 md:gap-4 mb-4 border border-gray-200 p-3 md:p-4"
      style="border-radius: {colorSettings.statsSectionBorderRadius || '8px'};"
    >
      {#if showYourEntries}
        <div class="flex flex-col items-center gap-1 flex-1">
          <div class="md:hidden flex flex-col items-center gap-1">
            <div class="w-6 h-6">
              <svg
                class="md:w-[35px] md:h-[35px] w-[28px] h-[28px]"
                viewBox="0 0 35 35"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17.3283 19.9884V31.3217H5.995C5.995 25.0625 11.0691 19.9884 17.3283 19.9884ZM25.8283 30.6134L21.6649 32.8023L22.4601 28.1661L19.0917 24.8828L23.7465 24.2065L25.8283 19.9884L27.9101 24.2065L32.565 24.8828L29.1966 28.1661L29.9918 32.8023L25.8283 30.6134ZM17.3283 18.5717C12.6321 18.5717 8.82833 14.768 8.82833 10.0717C8.82833 5.37547 12.6321 1.57172 17.3283 1.57172C22.0246 1.57172 25.8283 5.37547 25.8283 10.0717C25.8283 14.768 22.0246 18.5717 17.3283 18.5717Z"
                  fill={colorSettings.primaryTextColor || "#000000"}
                />
              </svg>
            </div>
            {#if isLoading}
              <div class="h-7 w-16 bg-gray-200 animate-pulse rounded"></div>
              <div
                class="text-sm"
                style="color: {colorSettings.secondaryTextColor};"
              >
                {t("widget_stats_your_entries")}
              </div>
            {:else}
              <div
                class="font-semibold text-lg"
                style="color: {colorSettings.primaryTextColor};"
              >
                {numberWithCommas($statsStore.yourEntries)}
              </div>
              <div
                class="text-sm"
                style="color: {colorSettings.secondaryTextColor};"
              >
                {t("widget_stats_your_entries")}
              </div>
            {/if}
          </div>

          <div
            class="hidden md:flex items-center gap-2"
            class:flex-row-reverse={isRTL}
            class:gap-6={isRTL}
          >
            <div class="mr-2" class:mr-0={isRTL} class:ml-2={isRTL}>
              <svg
                class="md:w-[35px] md:h-[35px] w-[28px] h-[28px]"
                viewBox="0 0 35 35"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17.3283 19.9884V31.3217H5.995C5.995 25.0625 11.0691 19.9884 17.3283 19.9884ZM25.8283 30.6134L21.6649 32.8023L22.4601 28.1661L19.0917 24.8828L23.7465 24.2065L25.8283 19.9884L27.9101 24.2065L32.565 24.8828L29.1966 28.1661L29.9918 32.8023L25.8283 30.6134ZM17.3283 18.5717C12.6321 18.5717 8.82833 14.768 8.82833 10.0717C8.82833 5.37547 12.6321 1.57172 17.3283 1.57172C22.0246 1.57172 25.8283 5.37547 25.8283 10.0717C25.8283 14.768 22.0246 18.5717 17.3283 18.5717Z"
                  fill={colorSettings.primaryTextColor || "#000000"}
                />
              </svg>
            </div>
            <div>
              <div
                class="text-xs"
                style="color: {colorSettings.secondaryTextColor};"
              >
                {t("widget_stats_your_entries")}
              </div>
              {#if isLoading}
                <div class="h-7 w-16 bg-gray-200 animate-pulse rounded"></div>
              {:else}
                <div
                  class="font-semibold text-xl"
                  style="color: {colorSettings.primaryTextColor || '#000000'};"
                >
                  {numberWithCommas($statsStore.yourEntries)}
                </div>
              {/if}
            </div>
          </div>
        </div>
      {/if}

      {#if showYourEntries && (showTotalEntries || showTimeLeft)}
        <div class="w-px h-12 border-r border-gray-200"></div>
      {/if}

      {#if showTotalEntries}
        <div class="flex flex-col items-center gap-1 flex-1">
          <div class="md:hidden flex flex-col items-center gap-1">
            <div class="w-6 h-6">
              <svg
                class="md:w-[35px] md:h-[35px] w-[28px] h-[28px]"
                viewBox="0 0 35 35"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.5443 5.8217H29.9609V8.65503H11.5443V5.8217ZM7.29427 4.40503V8.65503H8.71094V10.0717H4.46094V8.65503H5.8776V5.8217H4.46094V4.40503H7.29427ZM4.46094 19.9884V16.4467H7.29427V15.7384H4.46094V14.3217H8.71094V17.8634H5.8776V18.5717H8.71094V19.9884H4.46094ZM7.29427 27.78H4.46094V26.3634H7.29427V25.655H4.46094V24.2384H8.71094V29.905H4.46094V28.4884H7.29427V27.78ZM11.5443 15.7384H29.9609V18.5717H11.5443V15.7384ZM11.5443 25.655H29.9609V28.4884H11.5443V25.655Z"
                  fill={colorSettings.primaryTextColor || "#000000"}
                />
              </svg>
            </div>
            {#if isLoading}
              <div class="h-7 w-16 bg-gray-200 animate-pulse rounded"></div>
              <div
                class="text-sm"
                style="color: {colorSettings.secondaryTextColor};"
              >
                {t("widget_stats_total_entries")}
              </div>
            {:else}
              <div
                class="font-semibold text-lg"
                style="color: {colorSettings.primaryTextColor || '#000000'};"
              >
                {numberWithCommas($statsStore.totalEntries)}
              </div>
              <div
                class="text-sm"
                style="color: {colorSettings.secondaryTextColor};"
              >
                {t("widget_stats_total_entries")}
              </div>
            {/if}
          </div>

          <div
            class="hidden md:flex items-center gap-2"
            class:flex-row-reverse={isRTL}
            class:gap-6={isRTL}
          >
            <div class="mr-2" class:mr-0={isRTL} class:ml-2={isRTL}>
              <svg
                class="md:w-[35px] md:h-[35px] w-[28px] h-[28px]"
                viewBox="0 0 35 35"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.5443 5.8217H29.9609V8.65503H11.5443V5.8217ZM7.29427 4.40503V8.65503H8.71094V10.0717H4.46094V8.65503H5.8776V5.8217H4.46094V4.40503H7.29427ZM4.46094 19.9884V16.4467H7.29427V15.7384H4.46094V14.3217H8.71094V17.8634H5.8776V18.5717H8.71094V19.9884H4.46094ZM7.29427 27.78H4.46094V26.3634H7.29427V25.655H4.46094V24.2384H8.71094V29.905H4.46094V28.4884H7.29427V27.78ZM11.5443 15.7384H29.9609V18.5717H11.5443V15.7384ZM11.5443 25.655H29.9609V28.4884H11.5443V25.655Z"
                  fill={colorSettings.primaryTextColor || "#000000"}
                />
              </svg>
            </div>
            <div>
              <div
                class="text-xs"
                style="color: {colorSettings.secondaryTextColor};"
              >
                {t("widget_stats_total_entries")}
              </div>
              {#if isLoading}
                <div class="h-7 w-16 bg-gray-200 animate-pulse rounded"></div>
              {:else}
                <div
                  class="font-semibold text-xl"
                  style="color: {colorSettings.primaryTextColor || '#000000'};"
                >
                  {numberWithCommas($statsStore.totalEntries)}
                </div>
              {/if}
            </div>
          </div>
        </div>
      {/if}

      {#if showTotalEntries && showTimeLeft}
        <div class="w-px h-12 border-r border-gray-200"></div>
      {/if}

      {#if showTimeLeft}
        <div class="flex flex-col items-center gap-1 flex-1">
          <div class="md:hidden flex flex-col items-center gap-1">
            <div class="w-6 h-6">
              <svg
                class="md:w-[35px] md:h-[35px] w-[28px] h-[28px]"
                viewBox="0 0 35 35"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.3385 1.57172V4.40505H4.67179C3.8894 4.40505 3.25513 5.03932 3.25513 5.82172V28.4884C3.25513 29.2708 3.8894 29.905 4.67179 29.905H15.6575C14.1021 27.9648 13.1718 25.5019 13.1718 22.8217C13.1718 16.5625 18.2459 11.4884 24.5051 11.4884C27.1853 11.4884 29.6482 12.4187 31.5885 13.9741V5.82172C31.5885 5.03932 30.9542 4.40505 30.1718 4.40505H24.5051V1.57172H21.6718V4.40505H13.1718V1.57172H10.3385ZM33.0051 22.8217C33.0051 27.5161 29.1995 31.3217 24.5051 31.3217C19.8107 31.3217 16.0051 27.5161 16.0051 22.8217C16.0051 18.1273 19.8107 14.3217 24.5051 14.3217C29.1995 14.3217 33.0051 18.1273 33.0051 22.8217ZM23.0885 17.155V23.4085L26.3367 26.6568L28.3402 24.6533L25.9218 22.2349V17.155H23.0885Z"
                  fill={colorSettings.primaryTextColor || "#000000"}
                />
              </svg>
            </div>
            {#if isLoading}
              <div class="h-7 w-16 bg-gray-200 animate-pulse rounded"></div>
              <div
                class="text-sm"
                style="color: {colorSettings.secondaryTextColor};"
              >
                {t("widget_stats_time_left")}
              </div>
            {:else}
              <div
                class="font-semibold text-lg whitespace-nowrap"
                style="color: {colorSettings.primaryTextColor || '#000000'};"
              >
                {$statsStore.timeLeft}
              </div>
              <div
                class="text-sm"
                style="color: {colorSettings.secondaryTextColor};"
              >
                {t("widget_stats_time_left")}
              </div>
            {/if}
          </div>

          <div
            class="hidden md:flex items-center gap-2"
            class:flex-row-reverse={isRTL}
            class:gap-6={isRTL}
          >
            <div class="mr-2" class:mr-0={isRTL} class:ml-2={isRTL}>
              <svg
                class="md:w-[35px] md:h-[35px] w-[28px] h-[28px]"
                viewBox="0 0 35 35"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.3385 1.57172V4.40505H4.67179C3.8894 4.40505 3.25513 5.03932 3.25513 5.82172V28.4884C3.25513 29.2708 3.8894 29.905 4.67179 29.905H15.6575C14.1021 27.9648 13.1718 25.5019 13.1718 22.8217C13.1718 16.5625 18.2459 11.4884 24.5051 11.4884C27.1853 11.4884 29.6482 12.4187 31.5885 13.9741V5.82172C31.5885 5.03932 30.9542 4.40505 30.1718 4.40505H24.5051V1.57172H21.6718V4.40505H13.1718V1.57172H10.3385ZM33.0051 22.8217C33.0051 27.5161 29.1995 31.3217 24.5051 31.3217C19.8107 31.3217 16.0051 27.5161 16.0051 22.8217C16.0051 18.1273 19.8107 14.3217 24.5051 14.3217C29.1995 14.3217 33.0051 18.1273 33.0051 22.8217ZM23.0885 17.155V23.4085L26.3367 26.6568L28.3402 24.6533L25.9218 22.2349V17.155H23.0885Z"
                  fill={colorSettings.primaryTextColor || "#000000"}
                />
              </svg>
            </div>
            <div>
              <div
                class="text-xs"
                style="color: {colorSettings.secondaryTextColor};"
              >
                {t("widget_stats_time_left")}
              </div>
              {#if isLoading}
                <div class="h-7 w-16 bg-gray-200 animate-pulse rounded"></div>
              {:else}
                <div
                  class="font-semibold text-xl whitespace-nowrap"
                  style="color: {colorSettings.primaryTextColor || '#000000'};"
                >
                  {$statsStore.timeLeft}
                </div>
              {/if}
            </div>
          </div>
        </div>
      {/if}
    </div>

    {#if competition.widgetSettings?.showLeaderboard ?? false}
      <button
        class="w-full -mt-2 mb-2 p-3 md:p-4 cursor-pointer border transition-colors text-left"
        style="{leaderboardButtonStyle}; border-radius: {colorSettings.statsSectionBorderRadius ||
          '8px'};"
        onmouseenter={(e) => {
          const target = e.target as HTMLElement
          if (target) {
            target.style.cssText += leaderboardButtonHoverStyle
          }
        }}
        onmouseleave={(e) => {
          const target = e.target as HTMLElement
          if (target) {
            target.style.cssText = leaderboardButtonStyle
          }
        }}
        onclick={() => (showLeaderboard = !showLeaderboard)}
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div
              class="flex items-center justify-center w-10 h-10 rounded-full"
              style={trophyIconStyle}
            >
              <Trophy
                class="h-5 w-5"
                style="color: {colorSettings.iconColor};"
              />
            </div>
            <div>
              {#if isLoadingRank}
                <div
                  class="h-10 w-[100px] bg-gray-200 animate-pulse rounded"
                ></div>
              {:else if userRankData && user}
                <div
                  class="font-semibold text-lg"
                  style="color: {colorSettings.primaryTextColor};"
                >
                  #{userRankData.user_rank}{#if competition.widgetSettings?.leaderboardSettings?.showTotalEntrants ?? true}
                    {` of `}{userRankData.total_participants}{/if}
                </div>
                {#if competition?.widgetSettings?.leaderboardSettings?.showUserWinPercentage ?? true}
                  <p
                    class="text-xs"
                    style="color: {colorSettings.leaderboardSecondaryColor};"
                  >
                    {(
                      (Number($statsStore.yourEntries) /
                        Number($statsStore.totalEntries)) *
                      100
                    ).toFixed(2)}% {t("widget_leaderboard_chance_of_winning")}
                  </p>
                {:else}
                  <div
                    class="text-sm"
                    style="color: {colorSettings.leaderboardSecondaryColor};"
                  >
                    {t("widget_leaderboard_your_position")}
                  </div>
                {/if}
              {:else}
                <div
                  class="font-semibold text-lg"
                  style="color: {colorSettings.primaryTextColor};"
                >
                  #-{#if competition.widgetSettings?.leaderboardSettings?.showTotalEntrants ?? true}
                    {` of `}{#if competition.widgetSettings?.leaderboardSettings?.groupByTeam}
                      {numberWithCommas(
                        competition.informationCollection?.teamOptions
                          ?.length || 0,
                      )} teams
                    {:else}
                      {numberWithCommas($statsStore.totalEntries)}
                    {/if}{/if}
                </div>
                <div
                  class="text-xs"
                  style="color: {colorSettings.leaderboardSecondaryColor};"
                >
                  {t("widget_leaderboard_enter_to_rank")}
                </div>
              {/if}
            </div>
          </div>
          <div class="flex items-center gap-2">
            <div class="text-right">
              {#if isLoadingRank}
                <!-- <div class="h-10 w-16 bg-gray-200 animate-pulse rounded"></div> -->
              {:else if userRankData && user && userRankData.entries_ahead_of_next > 0 && (competition.widgetSettings?.leaderboardSettings?.showEntriesAheadOfNext ?? true)}
                <div
                  class="text-sm font-medium"
                  style="color: {colorSettings.primaryTextColor};"
                >
                  +{userRankData.entries_ahead_of_next}
                  {t("widget_leaderboard_ahead")}
                </div>
                <div
                  class="text-xs"
                  style="color: {colorSettings.secondaryTextColor};"
                >
                  {t("widget_leaderboard_of_next_person")}
                </div>
              {:else if !user}
                <!-- Show time remaining for non-logged-in users -->
                <div
                  class="text-sm font-medium"
                  style="color: {colorSettings.primaryTextColor};"
                >
                  {$statsStore.timeLeft}
                </div>
                <div
                  class="text-xs"
                  style="color: {colorSettings.secondaryTextColor};"
                >
                  {t("widget_stats_time_left")}
                </div>
              {/if}
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke={colorSettings.secondaryTextColor}
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="transition-transform"
              class:rotate-90={showLeaderboard}
            >
              <polyline points="9,18 15,12 9,6"></polyline>
            </svg>
          </div>
        </div>
        <!-- 
      {#if !showLeaderboard}
        <div class="mt-2 text-xs text-gray-500 text-center">
          Click to view full leaderboard
        </div>
      {/if} -->
      </button>

      {#if showLeaderboard}
        <div class="mb-4">
          <WidgetLeaderboard {competition} {user} />
        </div>
      {/if}
    {/if}

    {#if !isLoading && showReferrals && $statsStore.referrals && $statsStore.referrals.length > 0}
      <div
        class="flex flex-col overflow-x-auto justify-start items-start gap-2 md:gap-4 mb-4 p-3 md:p-4 border border-gray-200"
        style="border-radius: {colorSettings.statsSectionBorderRadius ||
          '8px'};"
      >
        <div class="flex items-center gap-2" class:flex-row-reverse={isRTL}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="30"
            height="30"
            viewBox="0 0 16 16"
            class="mr-2"
            class:mr-0={isRTL}
            class:ml-2={isRTL}
          >
            <path
              fill={colorSettings.primaryTextColor}
              d="M13 3V1H3v2H0v2a3 3 0 0 0 3.9 2.862A5 5 0 0 0 7 9.899v3.1H6a2 2 0 0 0-2 2h8a2 2 0 0 0-2-2H9v-3.1a5.003 5.003 0 0 0 3.1-2.037A3 3 0 0 0 16 5V3h-3zM3 6.813A1.815 1.815 0 0 1 1.187 5V4H3v1c0 .628.116 1.229.327 1.782c-.106.019-.216.03-.327.03zM14.813 5a1.815 1.815 0 0 1-2.14 1.783A4.994 4.994 0 0 0 13 5.001v-1h1.813v1z"
            />
          </svg>
          <div class="ml-2" class:ml-0={isRTL} class:mr-2={isRTL}>
            <div
              class="text-xs"
              style="color: {colorSettings.secondaryTextColor};"
            >
              {t("widget_stats_your_referrals")}
            </div>
            <div
              class="font-semibold text-xl"
              style="color: {colorSettings.primaryTextColor};"
            >
              {numberWithCommas($statsStore.referralCount)}
            </div>
          </div>
        </div>
        <div class="border rounded w-full max-h-[200px] overflow-y-scroll">
          <table class="min-w-full divide-y">
            <thead class="sticky top-0 bg-gray-100">
              <tr>
                <th
                  scope="col"
                  class="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider"
                >
                  {t("widget_referrals_name")}
                </th>
                <th
                  scope="col"
                  class="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider"
                >
                  {t("widget_referrals_date")}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y border-t border-gray-200">
              {#each $statsStore.referrals as referral}
                <tr>
                  <td
                    class="px-4 py-2 whitespace-nowrap text-sm"
                    style="color: {colorSettings.primaryTextColor};"
                  >
                    {referral.participant_name}
                  </td>
                  <td
                    class="px-4 py-2 whitespace-nowrap text-sm"
                    style="color: {colorSettings.secondaryTextColor};"
                  >
                    {new Date(referral.created_at).toLocaleDateString()}
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
    {/if}
  {/if}
{/if}
